import { useEffect, useRef, useState } from "react";
import { LifeBuoy, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { apiFetch } from "../lib/api";

const MODULES = [
  { value: "sms", label: "SMS" },
  { value: "voice", label: "Voice" },
  { value: "chat", label: "Chatbot" },
  { value: "food", label: "Food Cost" },
  { value: "members", label: "Membership" },
  { value: "tips", label: "Tips" },
  { value: "reports", label: "Reports" },
  { value: "admin", label: "Admin" },
  { value: "other", label: "Other" },
];

type Props = { onClose: () => void };

export function SupportModal({ onClose }: Props) {
  const { me } = useAuth();
  const [name, setName] = useState(me?.display_name ?? me?.email ?? "");
  const [module, setModule] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!module) { setError("Please select an affected module."); return; }
    if (!description.trim()) { setError("Please describe the issue."); return; }
    if (description.length > 2000) { setError("Description must be under 2000 characters."); return; }

    setSubmitting(true);
    try {
      const res = await apiFetch("/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), module, description: description.trim() }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Error ${res.status}`);
      }
      setSent(true);
    } catch (e) {
      setError((e as Error).message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-tribal-red" aria-hidden />
            <h2 className="text-lg font-semibold text-white">Request Support</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {sent ? (
          <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/15">
              <LifeBuoy className="h-6 w-6 text-green-400" aria-hidden />
            </div>
            <p className="text-base font-medium text-white">Ticket submitted</p>
            <p className="text-sm text-tribal-muted">
              Your request has been sent. David will follow up via email.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 rounded-lg bg-tribal-red px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-xs font-medium text-tribal-muted">
                  Your name
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First Last"
                  className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-tribal-muted">
                  Affected module
                </label>
                <select
                  value={module}
                  onChange={(e) => setModule(e.target.value)}
                  className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
                >
                  <option value="" disabled>Select a module…</option>
                  {MODULES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-xs font-medium text-tribal-muted">
                    Description
                  </label>
                  <span className={`text-[10px] ${description.length > 2000 ? "text-red-400" : "text-tribal-muted"}`}>
                    {description.length}/2000
                  </span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what's happening, what you expected, and any steps to reproduce…"
                  rows={5}
                  className="w-full resize-none rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                />
              </div>

              {error && (
                <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
                  {error}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-tribal-border px-5 py-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-tribal-muted transition-colors hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || description.length > 2000}
                className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Submit Ticket"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
